import { apiClient, ApiError, getAuthToken } from '@/shared/data/client';
import { getErrorMessage } from '@/shared/data/errors';
import type {
  InviteNotificationDto,
  OrganisationInviteDto,
  OrganisationRole,
} from '@flowqa/shared';

const toApiError = (err: unknown): ApiError =>
  err instanceof ApiError ? err : new ApiError(getErrorMessage(err));

const ensureToken = (tokenOverride?: string | null): string => {
  const token = tokenOverride ?? getAuthToken();
  if (!token) {
    throw new ApiError('Not authenticated');
  }
  return token;
};

export const createOrganisationInvite = async (
  input: { organisationId: string; email: string; role?: OrganisationRole },
  tokenOverride?: string | null,
): Promise<OrganisationInviteDto> => {
  const authToken = ensureToken(tokenOverride);
  try {
    return await apiClient.post<OrganisationInviteDto>(
      `organisations/${input.organisationId}/invites`,
      {
        email: input.email.trim(),
        ...(input.role ? { role: input.role } : {}),
      },
      { token: authToken, retries: 1 },
    );
  } catch (err) {
    throw toApiError(err);
  }
};

export const queryInviteNotifications = async (
  tokenOverride?: string | null,
): Promise<InviteNotificationDto[]> => {
  const authToken = ensureToken(tokenOverride);
  try {
    const result = await apiClient.get<{ items: InviteNotificationDto[] }>(
      'invites/notifications',
      { token: authToken, retries: 1 },
    );
    return result.items;
  } catch (err) {
    throw toApiError(err);
  }
};

export const acceptInvite = async (
  input: { id: string },
  tokenOverride?: string | null,
): Promise<OrganisationInviteDto> => {
  const authToken = ensureToken(tokenOverride);
  try {
    return await apiClient.post<OrganisationInviteDto>(`invites/${input.id}/accept`, undefined, {
      token: authToken,
      retries: 1,
    });
  } catch (err) {
    throw toApiError(err);
  }
};

export const declineInvite = async (
  input: { id: string },
  tokenOverride?: string | null,
): Promise<OrganisationInviteDto> => {
  const authToken = ensureToken(tokenOverride);
  try {
    return await apiClient.post<OrganisationInviteDto>(`invites/${input.id}/decline`, undefined, {
      token: authToken,
      retries: 1,
    });
  } catch (err) {
    throw toApiError(err);
  }
};

export const withdrawInvite = async (
  input: { id: string },
  tokenOverride?: string | null,
): Promise<OrganisationInviteDto> => {
  const authToken = ensureToken(tokenOverride);
  try {
    return await apiClient.post<OrganisationInviteDto>(`invites/${input.id}/withdraw`, undefined, {
      token: authToken,
      retries: 1,
    });
  } catch (err) {
    throw toApiError(err);
  }
};
